(() => {
  const setupGuiltyCursor = () => {
    const cursor = document.querySelector('.gp-cursor');
    if (!cursor) return;
    const canHover = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (!canHover || reduceMotion) return;

    const label = cursor.querySelector('.gp-cursor__label');
    let targetX = window.innerWidth / 2;
    let targetY = window.innerHeight / 2;
    let currentX = targetX;
    let currentY = targetY;
    let isVisible = false;

    const setCursorLabel = (text) => {
      if (!label) return;
      label.textContent = text || '';
      cursor.classList.toggle('is-hovering', Boolean(text));
    };

    const parseColor = (value) => {
      if (!value || value === 'transparent' || value === 'rgba(0, 0, 0, 0)') return null;
      const match = value.match(/rgba?\(([^)]+)\)/);
      if (!match) return null;
      const channels = match[1].split(',').map((part) => Number.parseFloat(part.trim()));
      if (channels.length < 3 || channels.some((channel, index) => index < 3 && Number.isNaN(channel))) return null;
      if (channels.length > 3 && channels[3] === 0) return null;
      return {
        r: channels[0],
        g: channels[1],
        b: channels[2],
      };
    };

    const getElementBackground = (element) => {
      let current = element;
      while (current && current !== document.documentElement) {
        const color = parseColor(window.getComputedStyle(current).backgroundColor);
        if (color) return color;
        current = current.parentElement;
      }
      return parseColor(window.getComputedStyle(document.body).backgroundColor) || { r: 5, g: 5, b: 5 };
    };

    const setCursorTone = (element) => {
      const color = getElementBackground(element);
      const max = Math.max(color.r, color.g, color.b);
      const min = Math.min(color.r, color.g, color.b);
      const luminance = (0.2126 * color.r + 0.7152 * color.g + 0.0722 * color.b) / 255;
      const isRed = color.r > 80 && color.r > color.g * 1.45 && color.r > color.b * 1.45 && max - min > 38;

      cursor.classList.toggle('cursor-on-red', isRed);
      cursor.classList.toggle('cursor-on-light', !isRed && luminance > 0.58);
      cursor.classList.toggle('cursor-on-dark', !isRed && luminance <= 0.58);
    };

    const getHoverLabel = (element) => {
      if (!element) return '';
      if (element.closest('.gp-evidence-card, .gp-evidence-object')) return 'EVIDENCE';
      if (element.closest('.gp-product-card')) return 'VIEW CASE';
      const productSubmit = element.closest('.gp-product-form button[type="submit"]');
      if (productSubmit) return productSubmit.dataset.gpCursorLabel || 'SECURE';
      if (element.closest('.gp-title-menu__item')) return 'ENTER';
      if (element.closest('.gp-collection-card')) return 'EVIDENCE';
      if (element.closest('button, .gp-about-cta a, .gp-start-strip a, .gp-cart__summary a, .gp-contact__form button')) return 'CHARGE';
      if (element.closest('a')) return 'ENTER';
      return '';
    };

    const move = () => {
      currentX += (targetX - currentX) * 0.24;
      currentY += (targetY - currentY) * 0.24;
      cursor.style.transform = `translate3d(${currentX}px, ${currentY}px, 0)`;
      window.requestAnimationFrame(move);
    };

    document.body.classList.add('gp-cursor-ready');
    move();

    window.addEventListener('pointermove', (event) => {
      if (event.pointerType && event.pointerType !== 'mouse') return;
      targetX = event.clientX;
      targetY = event.clientY;
      if (!isVisible) {
        isVisible = true;
        cursor.classList.add('is-visible');
      }
      setCursorTone(event.target);
      setCursorLabel(getHoverLabel(event.target));
    }, { passive: true });

    window.addEventListener('pointerleave', () => {
      isVisible = false;
      cursor.classList.remove('is-visible', 'is-hovering', 'cursor-on-dark', 'cursor-on-light', 'cursor-on-red');
      setCursorLabel('');
    });
  };

  const setupGuiltySounds = () => {
    const audioRoot = document.querySelector('[data-gp-menu-audio]');
    const siteMusic = document.querySelector('[data-gp-site-music]');
    if (!audioRoot && !siteMusic) return;

    const hoverSource = audioRoot?.dataset.hoverSrc;
    const selectSource = audioRoot?.dataset.selectSrc;

    const canHover = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
    const toggle = document.querySelector('[data-gp-sound-toggle]');
    const toggleLabel = document.querySelector('[data-gp-sound-label]');
    const hoverAudio = hoverSource ? new Audio(hoverSource) : null;
    const selectAudio = selectSource ? new Audio(selectSource) : null;
    const mutedStorageKey = 'gp-site-music-muted';
    const startedStorageKey = 'gp-site-music-started';
    const timeStorageKey = 'gp-site-music-time';
    const legacyMuted = window.localStorage.getItem('gp-menu-sound-muted');
    let soundUnlocked = false;
    let muted = window.localStorage.getItem(mutedStorageKey) === 'true' ||
      (window.localStorage.getItem(mutedStorageKey) === null && legacyMuted === 'true');
    let musicStarted = window.localStorage.getItem(startedStorageKey) === 'true';
    let activeItem = null;
    let audioContext = null;
    let lastSelectSoundAt = 0;
    let fadeFrame = 0;
    const musicVolume = 0.22;
    const selectableSelector = [
      '.gp-title-menu__item',
      '.gp-product-card',
      '.gp-collection-card',
      'a[href]',
      'button:not([disabled])',
      '[role="button"]',
      '[data-gp-option-button]:not([disabled])',
      'input:not([disabled])',
      'select:not([disabled])',
      'textarea:not([disabled])',
    ].join(',');

    if (hoverAudio) {
      hoverAudio.volume = 0.75;
      hoverAudio.preload = 'auto';
    }
    if (selectAudio) {
      selectAudio.volume = 0.9;
      selectAudio.preload = 'auto';
    }
    if (siteMusic) siteMusic.volume = musicVolume;
    siteMusic?.addEventListener('error', () => {
      console.warn(`[GUILTY PARTY] Site music failed to load: ${siteMusic.currentSrc || siteMusic.src || 'gp-site-music.mp3'}`);
    });

    const saveMusicTime = () => {
      if (!siteMusic || !Number.isFinite(siteMusic.currentTime)) return;
      window.localStorage.setItem(timeStorageKey, String(siteMusic.currentTime));
    };

    const restoreMusicTime = () => {
      if (!siteMusic) return;
      const savedTime = Number.parseFloat(window.localStorage.getItem(timeStorageKey) || '0');
      if (!Number.isFinite(savedTime) || savedTime <= 0) return;
      const duration = Number.isFinite(siteMusic.duration) ? siteMusic.duration : 0;
      siteMusic.currentTime = duration > 0 ? savedTime % duration : savedTime;
    };

    const fadeMusicIn = () => {
      if (!siteMusic) return;
      window.cancelAnimationFrame(fadeFrame);
      const startedAt = window.performance.now();
      siteMusic.volume = 0;
      const step = (now) => {
        const progress = Math.min(Math.max((now - startedAt) / 1400, 0), 1);
        siteMusic.volume = musicVolume * progress;
        if (progress < 1 && !siteMusic.paused) fadeFrame = window.requestAnimationFrame(step);
      };
      fadeFrame = window.requestAnimationFrame(step);
    };

    const playMusic = (shouldFade = false) => {
      if (!siteMusic || muted || !musicStarted) return Promise.resolve();
      return siteMusic.play().then(() => {
        if (shouldFade) fadeMusicIn();
        else siteMusic.volume = musicVolume;
      }).catch(() => {});
    };

    const pauseMusic = () => {
      if (!siteMusic) return;
      window.cancelAnimationFrame(fadeFrame);
      saveMusicTime();
      siteMusic.pause();
    };

    const updateToggle = () => {
      if (!toggle) return;
      toggle.classList.toggle('is-muted', muted);
      toggle.setAttribute('aria-pressed', muted ? 'false' : 'true');
      toggle.setAttribute('aria-label', muted ? 'Turn sound on' : 'Turn sound off');
      if (toggleLabel) toggleLabel.textContent = muted ? 'SOUND OFF' : 'SOUND ON';
    };

    const unlockSound = () => {
      soundUnlocked = true;
      if (musicStarted && !muted && siteMusic?.paused) playMusic(false);
    };

    const playFallbackTone = (type) => {
      if (!soundUnlocked || muted) return;
      const AudioContext = window.AudioContext || window.webkitAudioContext;
      if (!AudioContext) return;
      audioContext = audioContext || new AudioContext();

      const oscillator = audioContext.createOscillator();
      const gain = audioContext.createGain();
      const now = audioContext.currentTime;
      const isSelect = type === 'select';

      oscillator.type = 'triangle';
      oscillator.frequency.setValueAtTime(isSelect ? 190 : 520, now);
      oscillator.frequency.exponentialRampToValueAtTime(isSelect ? 310 : 720, now + (isSelect ? 0.11 : 0.055));
      gain.gain.setValueAtTime(0.0001, now);
      gain.gain.exponentialRampToValueAtTime(isSelect ? 0.3 : 0.22, now + 0.01);
      gain.gain.exponentialRampToValueAtTime(0.0001, now + (isSelect ? 0.15 : 0.075));

      oscillator.connect(gain);
      gain.connect(audioContext.destination);
      oscillator.start(now);
      oscillator.stop(now + (isSelect ? 0.16 : 0.085));
    };

    const playSound = (audio, type) => {
      if (!audio || !soundUnlocked || muted) return;
      if (type === 'select') {
        const now = window.performance.now();
        if (now - lastSelectSoundAt < 90) return;
        lastSelectSoundAt = now;
      }
      audio.pause();
      audio.currentTime = 0;
      audio.play().catch(() => {
        playFallbackTone(type);
      });
    };

    const getSelectable = (target) => {
      if (!target || typeof target.closest !== 'function') return null;
      const candidate = target.closest(selectableSelector);
      if (!candidate || candidate === audioRoot || audioRoot.contains(candidate)) return null;
      if (candidate.closest('[data-gp-audio-silent]')) return null;
      if (candidate.matches('[aria-disabled="true"], [disabled]')) return null;
      return candidate;
    };

    const setMenuVisualState = (item) => {
      document.querySelectorAll('.gp-title-menu__item.is-sound-hovered').forEach((candidate) => {
        candidate.classList.toggle('is-sound-hovered', candidate === item);
      });

      if (item && item.matches('.gp-title-menu__item')) {
        item.classList.add('is-sound-hovered');
      }
    };

    const selectItem = (item, shouldPlayHover) => {
      if (!item || activeItem === item) return;
      activeItem = item;
      setMenuVisualState(item);
      if (shouldPlayHover) playSound(hoverAudio, 'hover');
    };

    const clearItem = (item) => {
      if (activeItem !== item) return;
      activeItem = null;
      setMenuVisualState(null);
    };

    window.addEventListener('pointerdown', unlockSound, { once: true, passive: true });
    window.addEventListener('keydown', unlockSound, { once: true });

    if (siteMusic) {
      if (siteMusic.readyState >= 1) restoreMusicTime();
      else siteMusic.addEventListener('loadedmetadata', restoreMusicTime, { once: true });
      window.setInterval(saveMusicTime, 2000);
      window.addEventListener('pagehide', saveMusicTime);
      if (musicStarted && !muted) playMusic(false);
    }

    window.gpSiteAudio = {
      startFromIntro() {
        soundUnlocked = true;
        musicStarted = true;
        muted = false;
        window.localStorage.setItem(startedStorageKey, 'true');
        window.localStorage.setItem(mutedStorageKey, 'false');
        updateToggle();
        playMusic(true);
      },
      pauseForProductVideo() {
        const wasPlaying = Boolean(siteMusic && !siteMusic.paused && !muted);
        if (wasPlaying) pauseMusic();
        return wasPlaying;
      },
      resumeAfterProductVideo(shouldResume) {
        if (!shouldResume) return;
        soundUnlocked = true;
        playMusic(false);
      },
    };

    if (toggle) {
      toggle.addEventListener('click', () => {
        soundUnlocked = true;
        muted = !muted;
        musicStarted = true;
        window.localStorage.setItem(startedStorageKey, 'true');
        window.localStorage.setItem(mutedStorageKey, muted ? 'true' : 'false');
        window.localStorage.setItem('gp-menu-sound-muted', muted ? 'true' : 'false');
        if (muted) pauseMusic();
        else playMusic(true);
        updateToggle();
      });
    }

    document.addEventListener('pointerover', (event) => {
      if (!canHover || (event.pointerType && event.pointerType !== 'mouse')) return;
      const item = getSelectable(event.target);
      if (!item) return;
      selectItem(item, true);
    }, { passive: true });

    document.addEventListener('pointerout', (event) => {
      if (!activeItem || activeItem.contains(event.relatedTarget)) return;
      clearItem(activeItem);
    }, { passive: true });

    document.addEventListener('focusin', (event) => {
      const item = getSelectable(event.target);
      if (!item) return;
      selectItem(item, true);
    });

    document.addEventListener('focusout', (event) => {
      if (!activeItem || activeItem.contains(event.relatedTarget)) return;
      clearItem(activeItem);
    });

    document.addEventListener('click', (event) => {
      const item = getSelectable(event.target);
      if (!item) return;
      soundUnlocked = true;
      playSound(selectAudio, 'select');
    });

    document.addEventListener('keydown', (event) => {
      if (event.key !== 'Enter' && event.key !== ' ') return;
      const item = getSelectable(event.target);
      if (!item) return;
      soundUnlocked = true;
      playSound(selectAudio, 'select');
    });

    updateToggle();
  };

  const setupTitlePlayMenu = (root = document) => {
    root.querySelectorAll('[data-gp-play-menu]').forEach((menu) => {
      if (menu.dataset.gpPlayReady === 'true') return;

      const toggle = menu.querySelector('.gp-title-menu__play-toggle');
      const submenu = menu.querySelector('.gp-title-menu__submenu');
      const links = [...menu.querySelectorAll('.gp-title-menu__subitem[href]')];
      if (!toggle || !submenu) return;

      const setOpen = (isOpen, returnFocus = false) => {
        menu.classList.toggle('is-open', isOpen);
        toggle.setAttribute('aria-expanded', String(isOpen));
        submenu.setAttribute('aria-hidden', String(!isOpen));
        submenu.inert = !isOpen;
        links.forEach((link) => {
          if (isOpen) link.removeAttribute('tabindex');
          else link.setAttribute('tabindex', '-1');
        });
        if (!isOpen && returnFocus) toggle.focus();
      };

      toggle.addEventListener('click', () => {
        setOpen(toggle.getAttribute('aria-expanded') !== 'true');
      });

      toggle.addEventListener('keydown', (event) => {
        if (event.key !== 'Enter' && event.key !== ' ' && event.key !== 'Spacebar') return;
        event.preventDefault();
        setOpen(toggle.getAttribute('aria-expanded') !== 'true');
      });

      menu.addEventListener('keydown', (event) => {
        if (event.key !== 'Escape' || toggle.getAttribute('aria-expanded') !== 'true') return;
        event.preventDefault();
        setOpen(false, true);
      });

      setOpen(false);
      menu.dataset.gpPlayReady = 'true';
    });
  };

  const setupEvidenceArchive = (root = document) => {
    root.querySelectorAll('[data-gp-shop-archive]').forEach((archive) => {
      if (archive.dataset.gpArchiveReady === 'true') return;

      const cards = [...archive.querySelectorAll('[data-gp-archive-card]')];
      const filterButtons = [...archive.querySelectorAll('[data-gp-archive-filter]')];
      const status = archive.querySelector('[data-gp-archive-status]');
      const sortForm = archive.querySelector('[data-gp-archive-sort-form]');
      const sortSelect = archive.querySelector('[data-gp-archive-sort]');

      const applyFilter = (charge) => {
        let visibleCount = 0;
        cards.forEach((card) => {
          const isVisible = charge === 'all' || card.dataset.charge === charge;
          card.hidden = !isVisible;
          if (isVisible) visibleCount += 1;
        });

        filterButtons.forEach((button) => {
          const isActive = button.dataset.gpArchiveFilter === charge;
          button.classList.toggle('is-active', isActive);
          if (button.hasAttribute('aria-pressed')) button.setAttribute('aria-pressed', String(isActive));
        });

        if (status) {
          status.textContent = `${visibleCount} ${visibleCount === 1 ? 'FILE' : 'FILES'} ON RECORD`;
        }
      };

      filterButtons.forEach((button) => {
        button.addEventListener('click', () => applyFilter(button.dataset.gpArchiveFilter || 'all'));
      });

      sortSelect?.addEventListener('change', () => sortForm?.requestSubmit());
      archive.dataset.gpArchiveReady = 'true';
    });
  };



  const setupSiteIntro = () => {
    const intro = document.querySelector('[data-gp-site-intro]');
    if (!intro) return;

    const introVideo = intro.querySelector('[data-gp-intro-video]');
    const heroVideo = document.querySelector('[data-gp-hero-video]');
    const storageKey = 'gp-intro-seen';
    const forceIntro = new URLSearchParams(window.location.search).has('gp-title-screen');
    let hasEntered = false;
    let hasSeenIntro = false;
    let activeIntroMode = '';

    const getIntroMode = () => {
      return window.matchMedia('(max-width: 767px)').matches ? 'mobile' : 'desktop';
    };

    const checkAsset = (url, label, type = 'image') => {
      if (!url) return;
      if (type === 'image') {
        const image = new Image();
        image.addEventListener('error', () => {
          console.warn(`[GUILTY PARTY] ${label} failed to load: ${url}`);
        }, { once: true });
        image.src = url;
        return;
      }

      const media = document.createElement(type);
      media.preload = 'metadata';
      media.muted = true;
      media.addEventListener('error', () => {
        console.warn(`[GUILTY PARTY] ${label} failed to load: ${url}`);
      }, { once: true });
      media.src = url;
    };

    const configureIntroVideo = () => {
      if (!introVideo) return;
      const mode = getIntroMode();
      if (mode === activeIntroMode && introVideo.querySelector('source')) return;

      activeIntroMode = mode;
      const sourceUrl = mode === 'mobile' ? introVideo.dataset.gpMobileSrc : introVideo.dataset.gpDesktopSrc;
      const posterUrl = mode === 'mobile' ? introVideo.dataset.gpMobilePoster : introVideo.dataset.gpDesktopPoster;

      if (posterUrl) introVideo.setAttribute('poster', posterUrl);
      introVideo.querySelectorAll('source').forEach((source) => source.remove());

      if (sourceUrl) {
        const source = document.createElement('source');
        source.src = sourceUrl;
        source.type = 'video/mp4';
        source.addEventListener('error', () => {
          console.warn(`[GUILTY PARTY] Start-screen ${mode} video failed to load: ${sourceUrl}`);
        }, { once: true });
        introVideo.append(source);
        introVideo.load();
      } else {
        console.warn(`[GUILTY PARTY] Missing start-screen ${mode} video URL.`);
      }

      checkAsset(posterUrl, `Start-screen ${mode} poster`);
    };

    const warnStartVideoFailure = () => {
      const source = introVideo?.currentSrc || introVideo?.querySelector('source')?.src || 'gp-start-bg.mp4';
      console.warn(`[GUILTY PARTY] Start-screen video failed to load: ${source}`);
    };

    introVideo?.addEventListener('error', warnStartVideoFailure);
    introVideo?.querySelector('source')?.addEventListener('error', warnStartVideoFailure);

    try {
      hasSeenIntro = window.sessionStorage.getItem(storageKey) === 'true';
    } catch {}

    if (hasSeenIntro && !forceIntro) {
      document.documentElement.classList.remove('gp-intro-pending');
      intro.hidden = true;
      heroVideo?.play().catch(() => {});
      return;
    }

    configureIntroVideo();
    window.matchMedia('(max-width: 767px)').addEventListener?.('change', () => {
      if (intro.hidden || hasEntered) return;
      configureIntroVideo();
      introVideo?.play().catch(() => {});
    });

    document.documentElement.classList.remove('gp-intro-seen');
    document.documentElement.classList.add('gp-intro-pending');
    document.body.classList.add('gp-intro-open');
    introVideo?.play().catch(() => {});

    const enterSite = () => {
      if (hasEntered) return;
      hasEntered = true;
      try {
        window.sessionStorage.setItem(storageKey, 'true');
      } catch {}

      if (introVideo && heroVideo && Number.isFinite(introVideo.currentTime)) {
        try {
          heroVideo.currentTime = introVideo.currentTime;
        } catch {}
      }
      heroVideo?.play().catch(() => {});
      window.gpSiteAudio?.startFromIntro?.();
      intro.classList.add('is-leaving');
      document.documentElement.classList.remove('gp-intro-pending');
      document.body.classList.remove('gp-intro-open');

      window.setTimeout(() => {
        intro.hidden = true;
        document.documentElement.classList.add('gp-intro-seen');
      }, 280);
    };

    intro.addEventListener('pointerdown', () => {
      window.gpSiteAudio?.startFromIntro?.();
    }, { once: true });
    intro.addEventListener('click', enterSite);
    document.addEventListener('keydown', (event) => {
      if (hasEntered || (event.key !== 'Enter' && event.key !== ' ')) return;
      if (event.target?.matches?.('input, textarea, select')) return;
      event.preventDefault();
      enterSite();
    });

    document.addEventListener('click', (event) => {
      const resetLink = event.target.closest?.('[data-gp-title-screen-reset]');
      if (!resetLink) return;
      try {
        window.sessionStorage.removeItem(storageKey);
      } catch {}
    });
  };

  setupGuiltyCursor();
  setupGuiltySounds();
  setupTitlePlayMenu();
  setupEvidenceArchive();
  setupSiteIntro();
  document.addEventListener('shopify:section:load', (event) => {
    setupTitlePlayMenu(event.target);
    setupEvidenceArchive(event.target);
  });

  const setupTokenRunCountdown = () => {
    window.clearInterval(window.gpTokenRunCountdownInterval);
    window.gpTokenRunCountdownInterval = null;
    window.clearTimeout(window.gpTokenRunPurchaseCloseTimeout);
    window.gpTokenRunPurchaseCloseTimeout = null;
    window.gpTokenRunCountdownObserver?.disconnect();
    window.gpTokenRunCountdownObserver = null;

    const countdowns = [...document.querySelectorAll('[data-gp-token-countdown]')];
    const tokenRunEnabled = document.body.dataset.gpTokenRunEnabled === 'true';
    const deadline = Date.parse(document.body.dataset.gpTokenRunDeadline || '2026-08-09T18:00:00+05:30');
    const serverNowSeconds = Number.parseInt(
      countdowns[0]?.dataset.serverNow || document.body.dataset.gpServerNow || '',
      10,
    );
    const baselineKey = Number.isFinite(serverNowSeconds) ? serverNowSeconds : 'device';

    if (!window.gpTokenRunClockBaseline || window.gpTokenRunClockBaseline.key !== baselineKey) {
      window.gpTokenRunClockBaseline = {
        key: baselineKey,
        time: Number.isFinite(serverNowSeconds) ? serverNowSeconds * 1000 : Date.now(),
        performance: window.performance.now(),
      };
    }

    const getNow = () => {
      const baseline = window.gpTokenRunClockBaseline;
      return baseline.time + (window.performance.now() - baseline.performance);
    };

    const pad = (value) => String(Math.max(0, value)).padStart(2, '0');

    const countdownStates = countdowns.map((countdown) => ({
      countdown,
      fields: {
        days: countdown.querySelector('[data-gp-countdown-days]'),
        hours: countdown.querySelector('[data-gp-countdown-hours]'),
        minutes: countdown.querySelector('[data-gp-countdown-minutes]'),
        seconds: countdown.querySelector('[data-gp-countdown-seconds]'),
      },
      label: countdown.querySelector('[data-gp-countdown-label]'),
      active: countdown.querySelector('[data-gp-countdown-active]'),
      ended: countdown.querySelector('[data-gp-countdown-ended]'),
      isFinalHours: null,
    }));

    const setCountdownValues = (state, values) => {
      Object.entries(state.fields).forEach(([key, field]) => {
        if (!field) return;
        const nextValue = pad(values[key]);
        if (field.textContent !== nextValue) field.textContent = nextValue;
      });
    };

    const renderEnded = (state) => {
      setCountdownValues(state, { days: 0, hours: 0, minutes: 0, seconds: 0 });
      state.countdown.classList.remove('is-final-hours');
      state.countdown.classList.add('is-ended');
      if (state.active) state.active.hidden = true;
      if (state.ended) state.ended.hidden = false;
    };

    const closeTokenPurchases = () => {
      document.body.classList.add('gp-token-run-deadline-passed');
      document.body.classList.remove('gp-token-modal-open');

      document.querySelectorAll('[data-gp-token-run-active]').forEach((content) => {
        content.hidden = true;
      });

      const tokenRunPage = document.querySelector('.gp-token-run-page');
      tokenRunPage?.classList.add('gp-token-run-page--closed');
      tokenRunPage?.classList.remove('is-menu-open');
      tokenRunPage?.querySelector('[data-gp-token-menu-toggle]')?.setAttribute('aria-expanded', 'false');
      tokenRunPage?.querySelector('.gp-token-buy-panel')?.classList.add('gp-token-buy-panel--ended');

      document.querySelectorAll('[data-gp-token-purchase]').forEach((surface) => {
        surface.setAttribute('aria-disabled', 'true');
        if ('disabled' in surface) surface.disabled = true;
        surface.querySelectorAll('button, input, select, textarea').forEach((control) => {
          control.disabled = true;
        });
        surface.querySelectorAll('.accelerated-checkout-block').forEach((checkout) => {
          checkout.hidden = true;
        });
      });
    };

    window.gpTokenRunIsExpired = () => tokenRunEnabled && getNow() >= deadline;
    window.gpTokenRunClosePurchases = closeTokenPurchases;

    if (!window.gpTokenRunPurchaseGuardsInstalled) {
      const blockExpiredPurchase = (event) => {
        if (!event.target.closest?.('[data-gp-token-purchase]') || !window.gpTokenRunIsExpired?.()) return;
        window.gpTokenRunClosePurchases?.();
        event.preventDefault();
        event.stopImmediatePropagation();
      };

      document.addEventListener('submit', blockExpiredPurchase, true);
      document.addEventListener('click', blockExpiredPurchase, true);
      window.gpTokenRunPurchaseGuardsInstalled = true;
    }

    if (!tokenRunEnabled) {
      document.body.classList.remove('gp-token-run-deadline-passed');
      return;
    }

    const purchaseSurfaces = document.querySelectorAll('[data-gp-token-purchase]');
    const millisecondsUntilClose = deadline - getNow();
    if (purchaseSurfaces.length) {
      if (millisecondsUntilClose <= 0) {
        closeTokenPurchases();
      } else {
        window.gpTokenRunPurchaseCloseTimeout = window.setTimeout(
          closeTokenPurchases,
          Math.min(millisecondsUntilClose, 2147483647),
        );
      }
    }

    if (!countdowns.length) return;

    const stopCountdown = () => {
      window.clearInterval(window.gpTokenRunCountdownInterval);
      window.gpTokenRunCountdownInterval = null;
      window.gpTokenRunCountdownObserver?.disconnect();
      window.gpTokenRunCountdownObserver = null;
    };

    const renderCountdown = () => {
      const remainingMilliseconds = Math.max(0, deadline - getNow());
      const totalSeconds = Math.ceil(remainingMilliseconds / 1000);
      const values = {
        days: Math.floor(totalSeconds / 86400),
        hours: Math.floor((totalSeconds % 86400) / 3600),
        minutes: Math.floor((totalSeconds % 3600) / 60),
        seconds: totalSeconds % 60,
      };
      const isFinalHours = remainingMilliseconds > 0 && remainingMilliseconds < 86400000;

      countdownStates.forEach((state) => {
        setCountdownValues(state, values);
        if (state.isFinalHours === isFinalHours) return;
        state.isFinalHours = isFinalHours;
        state.countdown.classList.toggle('is-final-hours', isFinalHours);
        if (state.label) state.label.textContent = isFinalHours ? 'TOKEN RUN \u2014 FINAL HOURS' : 'TOKEN RUN ENDS IN';
      });

      if (remainingMilliseconds <= 0) {
        countdownStates.forEach(renderEnded);
        closeTokenPurchases();
        stopCountdown();
      }
    };

    renderCountdown();
    if (getNow() < deadline) {
      window.gpTokenRunCountdownInterval = window.setInterval(renderCountdown, 1000);
      window.gpTokenRunCountdownObserver = new MutationObserver(() => {
        if (!document.querySelector('[data-gp-token-countdown]')) stopCountdown();
      });
      window.gpTokenRunCountdownObserver.observe(document.body, { childList: true, subtree: true });
    }
  };

  window.setupGpTokenRunCountdown = setupTokenRunCountdown;
  setupTokenRunCountdown();
  if (window.gpTokenRunSectionLoadHandler) {
    document.removeEventListener('shopify:section:load', window.gpTokenRunSectionLoadHandler);
  }
  window.gpTokenRunSectionLoadHandler = () => setupTokenRunCountdown();
  document.addEventListener('shopify:section:load', window.gpTokenRunSectionLoadHandler);

  const setupTokenRunModal = () => {
    const modal = document.querySelector('[data-gp-token-modal]');
    if (!modal) return;

    const openers = [...document.querySelectorAll('[data-gp-token-modal-open]')];
    const closers = [...modal.querySelectorAll('[data-gp-token-modal-close]')];
    let previousFocus = null;

    const openModal = () => {
      previousFocus = document.activeElement;
      modal.hidden = false;
      document.body.classList.add('gp-token-modal-open');
      modal.querySelector('.gp-token-modal__close, .gp-token-modal__x')?.focus();
    };

    const closeModal = () => {
      modal.hidden = true;
      document.body.classList.remove('gp-token-modal-open');
      previousFocus?.focus?.();
    };

    openers.forEach((opener) => {
      opener.addEventListener('click', openModal);
    });

    closers.forEach((closer) => {
      closer.addEventListener('click', closeModal);
    });

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && !modal.hidden) closeModal();
    });
  };

  setupTokenRunModal();

  const setupTokenRunMobileMenu = () => {
    const screen = document.querySelector('.gp-token-run-screen');
    const toggle = document.querySelector('[data-gp-token-menu-toggle]');
    if (!screen || !toggle) return;

    toggle.addEventListener('click', () => {
      const isOpen = screen.classList.toggle('is-menu-open');
      toggle.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
    });

    document.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape' || !screen.classList.contains('is-menu-open')) return;
      screen.classList.remove('is-menu-open');
      toggle.setAttribute('aria-expanded', 'false');
    });
  };

  setupTokenRunMobileMenu();

  const setupEvidencePool = () => {
    const pool = document.querySelector('[data-gp-evidence-pool]');
    if (!pool) return;

    const startButton = pool.querySelector('[data-gp-pool-start]');
    const statusPanel = pool.querySelector('[data-gp-pool-status]');
    const cards = [...pool.querySelectorAll('[data-gp-evidence-card]')];
    const resultPanel = pool.querySelector('[data-gp-pool-result]');
    const chargeTarget = pool.querySelector('[data-gp-pool-charge]');
    const discountTarget = pool.querySelector('[data-gp-pool-discount]');
    const codeTarget = pool.querySelector('[data-gp-pool-code]');
    const shopLink = pool.querySelector('[data-gp-pool-shop]');
    const storageKey = 'gp-evidence-pool-result';
    const outcomes = [
      { code: 'FIRST5', rarity: 'COMMON', name: 'FIRST OFFENCE', discount: '5% OFF' },
      { code: 'REPEAT10', rarity: 'UNCOMMON', name: 'REPEAT OFFENDER', discount: '10% OFF' },
      { code: 'WANTED12', rarity: 'RARE', name: 'MOST WANTED', discount: '12% OFF' },
      { code: 'CERTIFIED15', rarity: 'LEGENDARY', name: 'GUILTY CERTIFIED', discount: '15% OFF' },
      { code: 'CASESHIP', rarity: 'SECRET', name: 'CASE CLOSED', discount: 'FREE SHIPPING' },
    ];

    if (!cards.length || !resultPanel || !chargeTarget || !discountTarget || !codeTarget) return;

    window.gpResetEvidencePool = () => {
      window.localStorage.removeItem(storageKey);
      window.location.reload();
    };

    const warnMissingEvidenceImage = (image) => {
      const assetName = image.dataset.gpAssetName || image.getAttribute('src') || 'unknown evidence asset';
      console.warn(`[GUILTY PARTY] Missing Evidence Pool asset: ${assetName}`);
    };

    const markMissingImage = (image) => {
      if (!image || image.dataset.gpMissingHandled === 'true') return;
      image.dataset.gpMissingHandled = 'true';
      warnMissingEvidenceImage(image);
    };

    pool.querySelectorAll('[data-gp-evidence-object-image]').forEach((image) => {
      image.addEventListener('error', () => {
        markMissingImage(image);
        image.closest('[data-gp-evidence-card]')?.classList.add('is-missing-image');
      });
      if (image.complete && image.naturalWidth === 0) {
        markMissingImage(image);
        image.closest('[data-gp-evidence-card]')?.classList.add('is-missing-image');
      }
    });

    pool.querySelectorAll('[data-gp-evidence-scene-image]').forEach((image) => {
      image.addEventListener('error', () => {
        markMissingImage(image);
        image.classList.add('is-missing-image');
      });
      if (image.complete && image.naturalWidth === 0) {
        markMissingImage(image);
        image.classList.add('is-missing-image');
      }
    });

    const readSavedResult = () => {
      try {
        return JSON.parse(window.localStorage.getItem(storageKey) || 'null');
      } catch {
        return null;
      }
    };

    const setCardsLocked = (selectedEvidence) => {
      cards.forEach((card) => {
        const isSelected = card.dataset.evidenceName === selectedEvidence;
        card.disabled = true;
        card.classList.toggle('is-selected', isSelected);
        card.classList.toggle('is-locked', !isSelected);
      });
    };

    const copyDiscountCode = async (code) => {
      if (!code) return;
      if (window.navigator?.clipboard?.writeText) {
        await window.navigator.clipboard.writeText(code);
        return;
      }

      const fallbackInput = document.createElement('input');
      fallbackInput.value = code;
      fallbackInput.setAttribute('readonly', '');
      fallbackInput.style.position = 'fixed';
      fallbackInput.style.left = '-9999px';
      document.body.append(fallbackInput);
      fallbackInput.select();
      document.execCommand('copy');
      fallbackInput.remove();
    };

    const renderResult = (result, fromStorage = false) => {
      if (!result) return;
      resultPanel.hidden = false;
      resultPanel.classList.remove('is-revealed');
      chargeTarget.textContent = `${result.rarity} - ${result.name}`;
      discountTarget.textContent = result.discount;
      codeTarget.textContent = result.code;
      setCardsLocked(result.evidence);
      pool.classList.add('has-result');

      if (shopLink) {
        const redirectTarget = shopLink.dataset.shopRedirect || '/collections/all';
        shopLink.dataset.gpDiscountCode = result.code;
        shopLink.href = `/discount/${encodeURIComponent(result.code)}?redirect=${encodeURIComponent(redirectTarget)}`;
      }

      if (statusPanel) {
        statusPanel.hidden = !fromStorage;
        statusPanel.classList.toggle('is-visible', fromStorage);
      }

      if (startButton) {
        startButton.hidden = fromStorage;
        startButton.textContent = fromStorage ? 'Evidence Already Pulled' : 'Charge Revealed';
        startButton.disabled = true;
      }

      window.requestAnimationFrame(() => {
        resultPanel.classList.add('is-revealed');
      });

      if (!fromStorage) {
        window.setTimeout(() => {
          resultPanel.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 260);
      }
    };

    shopLink?.addEventListener('click', async (event) => {
      const code = shopLink.dataset.gpDiscountCode || codeTarget.textContent.trim();
      if (!code) return;
      const targetUrl = shopLink.href;
      event.preventDefault();

      try {
        await copyDiscountCode(code);
        shopLink.textContent = 'Code Copied';
      } catch {
        shopLink.textContent = 'Use Code At Checkout';
      }

      window.setTimeout(() => {
        window.location.href = targetUrl;
      }, 120);
    });

    const savedResult = readSavedResult();
    if (savedResult?.code) {
      renderResult(savedResult, true);
      return;
    }

    startButton?.addEventListener('click', () => {
      pool.classList.add('is-armed');
      startButton.textContent = 'Pick Sealed Evidence';
      cards[0]?.focus();
    });

    cards.forEach((card) => {
      card.addEventListener('click', () => {
        if (readSavedResult()?.code) return;
        const outcome = outcomes[Math.floor(Math.random() * outcomes.length)];
        const result = {
          ...outcome,
          evidence: card.dataset.evidenceName,
          playedAt: new Date().toISOString(),
        };
        try {
          window.localStorage.setItem(storageKey, JSON.stringify(result));
        } catch {}
        renderResult(result);
      });
    });
  };

  setupEvidencePool();

  document.querySelectorAll('[data-gp-product-tabs]').forEach((tabsRoot) => {
    const tabs = [...tabsRoot.querySelectorAll('[data-gp-tab]')];
    const panels = [...tabsRoot.querySelectorAll('[data-gp-tab-panel]')];
    if (tabs.length === 0 || panels.length === 0) return;

    tabs.forEach((tab) => {
      tab.addEventListener('click', () => {
        const nextPanel = tab.dataset.gpTab;
        tabs.forEach((candidate) => {
          const isActive = candidate === tab;
          candidate.classList.toggle('is-active', isActive);
          candidate.setAttribute('aria-selected', isActive ? 'true' : 'false');
        });

        panels.forEach((panel) => {
          const isActive = panel.dataset.gpTabPanel === nextPanel;
          panel.classList.toggle('is-active', isActive);
          panel.hidden = !isActive;
        });
      });
    });
  });

  document.querySelectorAll('[data-gp-product-gallery]').forEach((gallery) => {
    const track = gallery.querySelector('[data-gp-gallery-track]');
    const slides = [...gallery.querySelectorAll('[data-gp-gallery-slide]')];
    const prev = gallery.querySelector('[data-gp-gallery-prev]');
    const next = gallery.querySelector('[data-gp-gallery-next]');
    const dots = [...gallery.querySelectorAll('[data-gp-gallery-dot]')];
    if (!track || slides.length === 0) return;

    let index = 0;
    let startX = 0;
    let currentX = 0;
    let isDragging = false;
    let didSwipe = false;

    const goTo = (nextIndex) => {
      index = (nextIndex + slides.length) % slides.length;
      track.style.transform = `translateX(-${index * 100}%)`;
      dots.forEach((dot, dotIndex) => {
        dot.setAttribute('aria-current', dotIndex === index ? 'true' : 'false');
      });
    };

    const goToMedia = (mediaId, imageId) => {
      if (!mediaId && !imageId) return;
      const nextIndex = slides.findIndex((slide) => {
        return String(slide.dataset.mediaId) === String(mediaId) || String(slide.dataset.imageId) === String(imageId);
      });
      if (nextIndex >= 0) goTo(nextIndex);
    };

    const openZoom = (initialIndex, opener) => {
      const galleryImages = slides.map((slide) => {
        const image = slide.querySelector('img');
        return {
          src: slide.dataset.gpZoomSrc || image?.currentSrc || image?.src || '',
          alt: image?.alt || '',
        };
      });
      if (!galleryImages[initialIndex]?.src) return;

      const zoom = document.createElement('div');
      const frame = document.createElement('div');
      const closeButton = document.createElement('button');
      const previousButton = document.createElement('button');
      const nextButton = document.createElement('button');
      const counter = document.createElement('div');
      const zoomImage = document.createElement('img');
      let activeIndex = initialIndex;
      let isTransitioning = false;
      let isClosed = false;
      let gesture = null;
      let ignoreNextOverlayClick = false;
      let adjacentPreloads = new Map();

      zoom.className = 'gp-image-zoom';
      zoom.setAttribute('role', 'dialog');
      zoom.setAttribute('aria-modal', 'true');
      zoom.setAttribute('aria-label', 'Expanded product images');
      frame.className = 'gp-image-zoom__frame';
      closeButton.className = 'gp-image-zoom__close';
      closeButton.type = 'button';
      closeButton.setAttribute('aria-label', 'Close expanded image');
      closeButton.textContent = 'X';

      previousButton.className = 'gp-image-zoom__nav gp-image-zoom__nav--previous';
      previousButton.type = 'button';
      previousButton.setAttribute('aria-label', 'Previous product image');
      previousButton.textContent = '\u2039';

      nextButton.className = 'gp-image-zoom__nav gp-image-zoom__nav--next';
      nextButton.type = 'button';
      nextButton.setAttribute('aria-label', 'Next product image');
      nextButton.textContent = '\u203a';

      counter.className = 'gp-image-zoom__counter';
      counter.setAttribute('role', 'status');
      counter.setAttribute('aria-live', 'polite');
      counter.setAttribute('aria-atomic', 'true');

      zoomImage.draggable = false;
      frame.append(zoomImage);
      zoom.append(closeButton, frame, previousButton, nextButton, counter);

      const createPreload = (imageIndex) => {
        const preloadImage = new Image();
        const promise = new Promise((resolve) => {
          preloadImage.addEventListener('load', () => resolve(true), { once: true });
          preloadImage.addEventListener('error', () => resolve(false), { once: true });
          preloadImage.src = galleryImages[imageIndex].src;
          if (preloadImage.complete) resolve(true);
        });
        return { image: preloadImage, promise };
      };

      const preloadAdjacent = () => {
        const adjacentIndexes = [activeIndex - 1, activeIndex + 1].filter(
          (imageIndex) => imageIndex >= 0 && imageIndex < galleryImages.length,
        );
        const nextPreloads = new Map();
        adjacentIndexes.forEach((imageIndex) => {
          nextPreloads.set(imageIndex, adjacentPreloads.get(imageIndex) || createPreload(imageIndex));
        });
        adjacentPreloads = nextPreloads;
      };

      const updateControls = () => {
        const atFirstImage = activeIndex === 0;
        const atLastImage = activeIndex === galleryImages.length - 1;
        previousButton.hidden = atFirstImage;
        previousButton.disabled = atFirstImage || isTransitioning;
        previousButton.setAttribute('aria-disabled', String(previousButton.disabled));
        nextButton.hidden = atLastImage;
        nextButton.disabled = atLastImage || isTransitioning;
        nextButton.setAttribute('aria-disabled', String(nextButton.disabled));
        counter.textContent = `${String(activeIndex + 1).padStart(2, '0')} / ${String(galleryImages.length).padStart(2, '0')}`;
      };

      const setActiveImage = () => {
        const activeImage = galleryImages[activeIndex];
        zoomImage.src = activeImage.src;
        zoomImage.alt = activeImage.alt;
      };

      const navigate = async (nextIndex) => {
        if (
          isClosed ||
          isTransitioning ||
          nextIndex < 0 ||
          nextIndex >= galleryImages.length ||
          nextIndex === activeIndex
        ) {
          return;
        }

        isTransitioning = true;
        updateControls();
        const direction = nextIndex > activeIndex ? 'next' : 'previous';
        const preload = adjacentPreloads.get(nextIndex) || createPreload(nextIndex);
        await preload.promise;
        if (isClosed) return;

        zoomImage.classList.add(`is-leaving-${direction}`);
        window.setTimeout(() => {
          if (isClosed) return;
          activeIndex = nextIndex;
          setActiveImage();
          updateControls();
          preloadAdjacent();
          zoomImage.classList.remove(`is-leaving-${direction}`);
          zoomImage.classList.add(`is-entering-${direction}`);
          void zoomImage.offsetWidth;
          window.requestAnimationFrame(() => {
            zoomImage.classList.remove(`is-entering-${direction}`);
            window.setTimeout(() => {
              if (isClosed) return;
              isTransitioning = false;
              updateControls();
            }, 110);
          });
        }, 110);
      };

      const close = () => {
        if (isClosed) return;
        isClosed = true;
        goTo(activeIndex);
        zoom.classList.remove('is-open');
        document.removeEventListener('keydown', handleKeydown);
        window.setTimeout(() => {
          zoom.remove();
          opener?.focus({ preventScroll: true });
        }, 180);
      };

      const handleKeydown = (event) => {
        if (event.key === 'Escape') {
          event.preventDefault();
          close();
          return;
        }

        if (event.key === 'ArrowLeft') {
          event.preventDefault();
          navigate(activeIndex - 1);
          return;
        }

        if (event.key === 'ArrowRight') {
          event.preventDefault();
          navigate(activeIndex + 1);
          return;
        }

        if (event.key !== 'Tab') return;
        const focusableControls = [...zoom.querySelectorAll('button:not([disabled]):not([hidden])')].filter(
          (control) => control.getClientRects().length > 0,
        );
        if (focusableControls.length === 0) return;

        const firstControl = focusableControls[0];
        const lastControl = focusableControls[focusableControls.length - 1];
        if (event.shiftKey && (document.activeElement === firstControl || !zoom.contains(document.activeElement))) {
          event.preventDefault();
          lastControl.focus();
        } else if (!event.shiftKey && (document.activeElement === lastControl || !zoom.contains(document.activeElement))) {
          event.preventDefault();
          firstControl.focus();
        }
      };

      zoom.addEventListener('click', (event) => {
        if (event.target.closest('.gp-image-zoom__close')) {
          close();
          return;
        }
        if (ignoreNextOverlayClick) {
          ignoreNextOverlayClick = false;
          return;
        }
        if (event.target === zoom) close();
      });

      zoom.addEventListener('pointerdown', (event) => {
        if ((event.pointerType === 'mouse' && event.button !== 0) || event.target.closest('button')) return;
        gesture = {
          pointerId: event.pointerId,
          startX: event.clientX,
          startY: event.clientY,
          currentX: event.clientX,
          currentY: event.clientY,
          moved: false,
        };
      });

      zoom.addEventListener('pointermove', (event) => {
        if (!gesture || gesture.pointerId !== event.pointerId) return;
        gesture.currentX = event.clientX;
        gesture.currentY = event.clientY;
        if (Math.hypot(gesture.currentX - gesture.startX, gesture.currentY - gesture.startY) > 10) {
          gesture.moved = true;
        }
      });

      zoom.addEventListener('pointerup', (event) => {
        if (!gesture || gesture.pointerId !== event.pointerId) return;
        const horizontalMovement = gesture.currentX - gesture.startX;
        const verticalMovement = gesture.currentY - gesture.startY;
        const isHorizontalSwipe =
          Math.abs(horizontalMovement) >= 50 && Math.abs(horizontalMovement) > Math.abs(verticalMovement);
        ignoreNextOverlayClick = gesture.moved;
        gesture = null;
        if (isHorizontalSwipe) navigate(activeIndex + (horizontalMovement < 0 ? 1 : -1));
      });

      zoom.addEventListener('pointercancel', () => {
        gesture = null;
      });

      previousButton.addEventListener('click', () => navigate(activeIndex - 1));
      nextButton.addEventListener('click', () => navigate(activeIndex + 1));

      setActiveImage();
      updateControls();
      preloadAdjacent();
      document.body.append(zoom);
      document.addEventListener('keydown', handleKeydown);
      window.requestAnimationFrame(() => zoom.classList.add('is-open'));
      closeButton.focus({ preventScroll: true });
    };

    gallery.gpGoToMedia = goToMedia;

    prev?.addEventListener('click', () => goTo(index - 1));
    next?.addEventListener('click', () => goTo(index + 1));
    dots.forEach((dot, dotIndex) => dot.addEventListener('click', () => goTo(dotIndex)));
    slides.forEach((slide, slideIndex) => {
      slide.addEventListener('click', () => {
        if (didSwipe) return;
        openZoom(slideIndex, slide);
      });
    });

    gallery.addEventListener('touchstart', (event) => {
      startX = event.touches[0].clientX;
      currentX = startX;
      isDragging = true;
      didSwipe = false;
    }, { passive: true });

    gallery.addEventListener('touchmove', (event) => {
      if (!isDragging) return;
      currentX = event.touches[0].clientX;
    }, { passive: true });

    gallery.addEventListener('touchend', () => {
      if (!isDragging) return;
      const diff = currentX - startX;
      if (Math.abs(diff) > 45) {
        didSwipe = true;
        goTo(index + (diff < 0 ? 1 : -1));
        window.setTimeout(() => {
          didSwipe = false;
        }, 180);
      }
      isDragging = false;
    });
  });

  const updateCartLine = async (line, quantity) => {
    await fetch('/cart/change.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({ line, quantity }),
    });
    window.location.reload();
  };

  document.querySelectorAll('[data-gp-cart-quantity]').forEach((quantity) => {
    const input = quantity.querySelector('input');
    const minus = quantity.querySelector('[data-gp-quantity-minus]');
    const plus = quantity.querySelector('[data-gp-quantity-plus]');
    if (!input || !minus || !plus) return;

    minus.addEventListener('click', () => {
      input.value = Math.max(0, Number(input.value || 0) - 1);
      updateCartLine(Number(input.dataset.gpLine), Number(input.value));
    });

    plus.addEventListener('click', () => {
      input.value = Number(input.value || 0) + 1;
      updateCartLine(Number(input.dataset.gpLine), Number(input.value));
    });

    input.addEventListener('change', () => {
      updateCartLine(Number(input.dataset.gpLine), Math.max(0, Number(input.value || 0)));
    });
  });

  document.querySelectorAll('[data-gp-cart-remove]').forEach((button) => {
    button.addEventListener('click', () => {
      updateCartLine(Number(button.dataset.gpLine), 0);
    });
  });

  document.querySelectorAll('.gp-token-buy-form[data-gp-token-purchase]').forEach((form) => {
    const buyButton = form.querySelector('[data-gp-token-buy-now]');
    const quantityInput = form.querySelector('input[name="quantity"]');
    const checkoutInput = form.querySelector('input[name="return_to"]');
    const errorMessage = form.querySelector('[data-gp-token-purchase-error]');
    const variantInputs = [...form.querySelectorAll('input[name="id"][type="radio"]')];
    let isProcessing = false;

    if (!buyButton || !quantityInput || !checkoutInput || variantInputs.length === 0) return;

    const getSelectedVariant = () => form.querySelector('input[name="id"][type="radio"]:checked:not(:disabled)');
    const isPurchaseOpen = () => {
      return document.body.dataset.gpTokenRunEnabled === 'true' && !window.gpTokenRunIsExpired?.();
    };
    const clearError = () => {
      if (!errorMessage) return;
      errorMessage.textContent = '';
      errorMessage.hidden = true;
    };
    const showError = (message) => {
      if (!errorMessage) return;
      errorMessage.textContent = message;
      errorMessage.hidden = false;
    };
    const syncButton = () => {
      const selectedVariant = getSelectedVariant();
      const hasValidSelection = Boolean(selectedVariant);
      buyButton.disabled = isProcessing || !hasValidSelection || !isPurchaseOpen();
      if (!isProcessing) {
        buyButton.textContent = hasValidSelection
          ? selectedVariant.dataset.gpTokenButtonLabel || 'BUY TOKEN NOW'
          : 'SELECT A TOKEN';
      }
    };

    variantInputs.forEach((input) => {
      input.addEventListener('change', () => {
        clearError();
        syncButton();
      });
    });

    form.addEventListener('submit', async (event) => {
      event.preventDefault();
      event.stopImmediatePropagation();

      if (isProcessing) return;

      const selectedVariant = getSelectedVariant();
      const quantity = Number.parseInt(quantityInput.value, 10) || 1;
      if (!selectedVariant) {
        syncButton();
        return;
      }
      if (!isPurchaseOpen()) {
        window.gpTokenRunClosePurchases?.();
        return;
      }

      isProcessing = true;
      clearError();
      buyButton.disabled = true;
      buyButton.textContent = 'OPENING CHECKOUT...';

      try {
        const cartAddUrl = form.action.endsWith('.js') ? form.action : `${form.action}.js`;
        const response = await fetch(cartAddUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          body: JSON.stringify({
            items: [{ id: selectedVariant.value, quantity }],
          }),
        });
        const result = await response.json().catch(() => null);

        if (!response.ok) {
          throw new Error(result?.description || result?.message || 'Unable to add this Token. Please try again.');
        }

        window.location.assign(checkoutInput.value || '/checkout');
      } catch (error) {
        isProcessing = false;
        showError(error?.message || 'Unable to add this Token. Please try again.');
        syncButton();
      }
    });

    syncButton();
  });

  document.querySelectorAll('.gp-product-form').forEach((form) => {
    const idInput = form.querySelector('input[name="id"]');
    const selects = [...form.querySelectorAll('.gp-product-form__select')];
    const optionButtons = [...form.querySelectorAll('[data-gp-option-button]')];
    const addToCartButton = form.querySelector('[data-gp-add-to-cart]');
    const buyNowButton = form.querySelector('[data-gp-buy-now]');
    const productJson = document.querySelector('[data-gp-product-json]');
    const gallery = document.querySelector('[data-gp-product-gallery]');

    if (!idInput || selects.length === 0 || !productJson) return;

    let product;
    try {
      product = JSON.parse(productJson.textContent);
    } catch {
      return;
    }

    const updateVariant = () => {
      const options = selects.map((select) => select.value);
      const variant = product.variants.find((candidate) => {
        return candidate.options.every((option, index) => option === options[index]);
      });

      if (!variant) {
        idInput.value = '';
        [addToCartButton, buyNowButton].forEach((button) => {
          if (!button) return;
          button.disabled = true;
          button.textContent = 'UNAVAILABLE';
        });
        return;
      }

      idInput.value = variant.id;
      if (addToCartButton) {
        addToCartButton.disabled = !variant.available;
        addToCartButton.textContent = variant.available ? 'ADD TO CART' : 'SOLD OUT';
        addToCartButton.value = `${window.location.pathname}?variant=${variant.id}`;
      }
      if (buyNowButton) {
        buyNowButton.disabled = !variant.available;
        buyNowButton.textContent = variant.available ? 'BUY NOW' : 'SOLD OUT';
      }

      const mediaId = variant.featured_media?.id;
      const imageId = variant.featured_image?.id;
      if (mediaId && typeof gallery?.gpGoToMedia === 'function') {
        gallery.gpGoToMedia(mediaId, imageId);
      } else if (imageId && typeof gallery?.gpGoToMedia === 'function') {
        gallery.gpGoToMedia(null, imageId);
      }
    };

    const syncOptionButtons = () => {
      optionButtons.forEach((button) => {
        const index = Number(button.dataset.optionIndex);
        const select = selects[index];
        button.classList.toggle('is-selected', Boolean(select && select.value === button.dataset.optionValue));
      });
    };

    optionButtons.forEach((button) => {
      button.addEventListener('click', () => {
        if (button.disabled) return;
        const index = Number(button.dataset.optionIndex);
        const select = selects[index];
        if (!select) return;
        select.value = button.dataset.optionValue;
        select.dispatchEvent(new Event('change', { bubbles: true }));
      });
    });

    selects.forEach((select) => select.addEventListener('change', () => {
      syncOptionButtons();
      updateVariant();
    }));

    syncOptionButtons();
    updateVariant();
  });
})();
